import React, { useEffect, useState, useMemo } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";

import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { postForm } from "../utils/api";
import { Link } from "react-router-dom";
import { StaticKv } from "../components/StaticKv";

import "../assets/styles/consulting.scss";

const PREF_OPTIONS = [
  "北海道",
  "青森県",
  "岩手県",
  "宮城県",
  "秋田県",
  "山形県",
  "福島県",
  "茨城県",
  "栃木県",
  "群馬県",
  "埼玉県",
  "千葉県",
  "東京都",
  "神奈川県",
  "新潟県",
  "富山県",
  "石川県",
  "福井県",
  "山梨県",
  "長野県",
  "岐阜県",
  "静岡県",
  "愛知県",
  "三重県",
  "滋賀県",
  "京都府",
  "大阪府",
  "兵庫県",
  "奈良県",
  "和歌山県",
  "鳥取県",
  "島根県",
  "岡山県",
  "広島県",
  "山口県",
  "徳島県",
  "香川県",
  "愛媛県",
  "高知県",
  "福岡県",
  "佐賀県",
  "長崎県",
  "熊本県",
  "大分県",
  "宮崎県",
  "鹿児島県",
  "沖縄県",
];

type TInputs = {
  name: string;
  furigana: string;
  post: string;
  prefecture: string;
  city: string;
  address: string;
  email: string;
  emailAgain: string;
  tel: string;
  message: string;
  privacy: boolean;
};

export const Contact = () => {
  const [step, setStep] = useState<number>(1);
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    getValues,
    reset,
  } = useForm<TInputs>();

  const onSubmit: SubmitHandler<TInputs> = async (data) => {
    try {
      const formData = {
        "your-name": data.name,
        "text-845": data.furigana,
        "text-6": data.post,
        "text-85": data.prefecture,
        "text-224": data.city,
        "text-372": data.address,
        "your-email": data.email,
        "tel-673": data.tel,
        "your-message": data.message,
      };

      await postForm(10, formData);
      reset();
      setStep(3);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const selectColor = useMemo(() => {
    if (watch().prefecture) return "#000";
    return "gray";
  }, [watch().prefecture]);

  return (
    <div className="page-consulting">
      <Header>
        <StaticKv title="contact" />
      </Header>
      <div className="consulting-main">
        {step === 1 && (
          <form>
            <h3>
              無料の不動産コンサルティング
              <br />
              申込みフォーム
            </h3>
            <p className="required-text">
              <span className="star">*</span>は必須項目です
            </p>
            <div className="input-box">
              <label htmlFor="name">
                お名前<span className="star">*</span>
              </label>
              <input
                id="name"
                placeholder="例）山田 太郎"
                {...register("name", {
                  required: "お名前を入力してください。",
                })}
                type="text"
              />
              <p className="error">
                <ErrorMessage errors={errors} name="name" />
              </p>
            </div>
            <div className="input-box">
              <label htmlFor="furigana">
                ふりがな<span className="star">*</span>
              </label>
              <input
                id="furigana"
                placeholder="例）やまだ たろう"
                {...register("furigana", {
                  required: "ふりがなを入力してください。",
                  pattern: {
                    value: /^[\u3040-\u309F]+$/,
                    message: "ふりがなの形式が不正です",
                  },
                })}
                type="text"
              />
              <p className="error">
                <ErrorMessage errors={errors} name="furigana" />
              </p>
            </div>
            <div className="input-box">
              <label htmlFor="post">
                郵便番号<span className="star">*</span>
                <small>（ハイフンなし）</small>
              </label>
              <input
                id="post"
                placeholder="例）1234567"
                {...register("post", {
                  required: "郵便番号を入力してください。",
                  pattern: {
                    value: /^[0-9]{3}-[0-9]{4}$/,
                    message: "郵便番号の形式が不正です",
                  },
                })}
                type="text"
              />
              <p className="error">
                <ErrorMessage errors={errors} name="post" />
              </p>
            </div>
            <div className="input-box">
              <label htmlFor="prefecture">
                都道府県<span className="star">*</span>
              </label>
              <select
                {...register("prefecture", {
                  required: "都道府県を選択してください。",
                })}
                style={{ color: selectColor }}
              >
                <option style={{ display: "none" }}>選択してください</option>;
                {PREF_OPTIONS.map((option, index) => {
                  return (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  );
                })}
              </select>
              <p className="error">
                <ErrorMessage errors={errors} name="prefecture" />
              </p>
            </div>
            <div className="input-box">
              <label htmlFor="city">
                市区郡<span className="star">*</span>
              </label>
              <input
                id="city"
                placeholder="例）千代田区"
                {...register("city", {
                  required: "市区郡を入力してください。",
                })}
                type="text"
              />
              <p className="error">
                <ErrorMessage errors={errors} name="city" />
              </p>
            </div>
            <div className="input-box">
              <label htmlFor="address">
                町名・番地<span className="star">*</span>
              </label>
              <input
                id="address"
                placeholder="例）外神田1-2-3"
                {...register("address", {
                  required: "町名・番地を入力してください。",
                })}
                type="text"
              />
              <p className="error">
                <ErrorMessage errors={errors} name="address" />
              </p>
            </div>
            <div className="input-box">
              <label htmlFor="email">
                メールアドレス<span className="star">*</span>
              </label>
              <input
                id="email"
                placeholder="xxxx@xxxx.com"
                {...register("email", {
                  required: "メールアドレスを入力してください",
                  maxLength: 60,
                  pattern: {
                    value: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
                    message: "メールアドレスの形式が不正です",
                  },
                })}
                type="email"
              />
              <p className="error">
                <ErrorMessage errors={errors} name="email" />
              </p>
            </div>
            <div className="input-box">
              <label htmlFor="emailAgain">
                メールアドレス（確認用）<span className="star">*</span>
              </label>
              <input
                id="emailAgain"
                placeholder="xxxx@xxxx.com"
                {...register("emailAgain", {
                  required: "確認のためメールアドレスを再入力してください。",
                  maxLength: 60,
                  validate: (value) => {
                    return (
                      value === getValues("email") ||
                      "メールアドレスが一致しません"
                    );
                  },
                })}
                type="email"
              />
              <p className="error">
                <ErrorMessage errors={errors} name="emailAgain" />
              </p>
            </div>
            <div className="input-box">
              <label htmlFor="tel">
                電話番号<span className="star">*</span>
                <small>（ハイフンなし）</small>
              </label>
              <input
                id="tel"
                placeholder="08012345678"
                {...register("tel", {
                  required: "電話番号を入力してください",
                  pattern: {
                    value: /^0\d{9,10}$/,
                    message: "電話番号の形式が不正です",
                  },
                })}
                type="tel"
              />
              <p className="error">
                <ErrorMessage errors={errors} name="tel" />
              </p>
            </div>
            <div className="input-box">
              <label htmlFor="message">ご相談内容</label>
              <textarea
                id="message"
                placeholder="簡単で結構ですのでご相談内容をご記入ください"
                {...register("message")}
              />
            </div>

            <div className="input-box privacy">
              <p>個人情報の取り扱いについて</p>
              <div className="privacy-box">
                <input
                  type="checkbox"
                  id="privacy"
                  {...register("privacy", {
                    required: true,
                  })}
                />
                <label htmlFor="privacy">
                  <Link to="/privacy">個人情報保護方針</Link>に同意する
                </label>
              </div>
            </div>

            <button onClick={() => setStep(2)}>確認画面へ</button>
          </form>
        )}
        {step === 2 && (
          <div className="confirm">
            <h3>
              入力の内容にお間違い無いか
              <br />
              ご確認をお願いします。
            </h3>
            <p>お名前：{getValues().name}</p>
            <p>ふりがな：{getValues().furigana}</p>
            <p>郵便番号：{getValues().post}</p>
            <p>都道府県：{getValues().prefecture}</p>
            <p>市区郡：{getValues().city}</p>
            <p>町名・番地：{getValues().address}</p>
            <p>メールアドレス：{getValues().email}</p>
            <p>電話番号：{getValues().tel}</p>
            <p>
              ご相談内容：:
              <br />
              {getValues().message}
            </p>
            <button type="submit" onClick={handleSubmit(onSubmit)}>
              送信する
            </button>
            <button onClick={() => setStep(1)} className="back">
              戻る
            </button>
          </div>
        )}
        {step === 3 && (
          <div className="confirm">
            <h3>
              お問い合わせありがとうございます。
              <br />
              内容を確認後、担当者よりご連絡させていただきます。
            </h3>
            <button>
              <Link to={"/"}>トップに戻る</Link>
            </button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};
